import { z } from "zod";

export const ORGANIZATION_ROLES = [
  "owner",
  "admin",
  "member",
  "viewer",
] as const;

export const FORMER_MEMBER_LABEL = "Former member";

export const ORGANIZATION_INVITE_ROLES = ["admin", "member", "viewer"] as const;

export const ORGANIZATION_PRODUCT_KEYS = [
  "website",
  "job_site_photos",
  "credentials",
] as const;

export const ORGANIZATION_INVITATION_STATUSES = [
  "pending",
  "accepted",
  "declined",
  "expired",
] as const;

export const OrganizationRoleSchema = z.enum(ORGANIZATION_ROLES);
export type OrganizationRole = z.infer<typeof OrganizationRoleSchema>;

export const OrganizationInviteRoleSchema = z.enum(ORGANIZATION_INVITE_ROLES);
export type OrganizationInviteRole = z.infer<
  typeof OrganizationInviteRoleSchema
>;

export const OrganizationProductKeySchema = z.enum(ORGANIZATION_PRODUCT_KEYS);
export type OrganizationProductKey = z.infer<
  typeof OrganizationProductKeySchema
>;

export const OrganizationInvitationStatusSchema = z.enum(
  ORGANIZATION_INVITATION_STATUSES,
);
export type OrganizationInvitationStatus = z.infer<
  typeof OrganizationInvitationStatusSchema
>;

export const OrganizationMembershipSchema = z.object({
  organizationId: z.string(),
  userId: z.string(),
  role: OrganizationRoleSchema,
  joinedAt: z.string().datetime().nullable().optional(),
  backend: z.boolean(),
  canCapturePhotos: z.boolean(),
});

export type OrganizationMembership = z.infer<
  typeof OrganizationMembershipSchema
>;

export const OrganizationRosterUserSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().email(),
  avatarUrl: z.string().nullable().optional(),
  role: OrganizationRoleSchema,
});

export type OrganizationRosterUser = z.infer<
  typeof OrganizationRosterUserSchema
>;

export const OrganizationRosterMemberSchema =
  OrganizationMembershipSchema.extend({
    id: z.string(),
    user: OrganizationRosterUserSchema,
  });

export type OrganizationRosterMember = z.infer<
  typeof OrganizationRosterMemberSchema
>;

export const OrganizationProductSchema = z.object({
  key: OrganizationProductKeySchema,
  billingStatus: z.string(),
  selectedAt: z.string().datetime().nullable().optional(),
});

export type OrganizationProduct = z.infer<typeof OrganizationProductSchema>;

export const OrganizationEntitlementsSchema = z.object({
  trialActive: z.boolean(),
  trialExpired: z.boolean(),
  daysUntilTrialEnd: z.number().int().nullable(),
  suitePaid: z.boolean(),
  website: z.boolean(),
  jobSitePhotos: z.boolean(),
  credentials: z.boolean(),
});

export type OrganizationEntitlements = z.infer<
  typeof OrganizationEntitlementsSchema
>;

export const OrganizationCapabilitiesSchema = z.object({
  canCapturePhotos: z.boolean(),
  canCreateJobs: z.boolean(),
  canManageJobs: z.boolean(),
  canManageAllPhotos: z.boolean(),
  canManageWebsite: z.boolean(),
  canUseCredentials: z.boolean(),
});

export type OrganizationCapabilities = z.infer<
  typeof OrganizationCapabilitiesSchema
>;

export const TradeOptionSchema = z.object({
  slug: z.string(),
  name: z.string(),
});

export type TradeOption = z.infer<typeof TradeOptionSchema>;

export const OrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string().nullable().optional(),
  tradeSlug: z.string().nullable().optional(),
  tradeType: z.string().nullable().optional(),
  billingAccountToken: z.string().uuid().optional(),
  timeZone: z.string().optional().default("UTC"),
  trialStartedAt: z.string().datetime().nullable().optional(),
  trialEndsAt: z.string().datetime().nullable().optional(),
  trialEndsAtDisplay: z.string().nullable().optional(),
  products: z.array(OrganizationProductSchema).optional().default([]),
  membership: OrganizationMembershipSchema.nullable().optional(),
  hasWebsite: z.boolean().optional(),
  entitlements: OrganizationEntitlementsSchema,
  capabilities: OrganizationCapabilitiesSchema.optional(),
  roleDescriptions: z.record(z.string(), z.string()).optional(),
});

export type Organization = z.infer<typeof OrganizationSchema>;

export const OTHER_TRADE_SLUG = "other";

export const CreateOrganizationSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    phone: z.string().trim().min(1).max(40),
    ownerName: z.string().trim().min(1).max(100),
    tradeSlug: z.string().trim().min(1).max(80),
    tradeType: z.string().trim().max(80).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.tradeSlug === OTHER_TRADE_SLUG && !value.tradeType?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter your trade",
        path: ["tradeType"],
      });
    }
  });

export type CreateOrganizationInput = z.infer<typeof CreateOrganizationSchema>;

export const UpdateOrganizationSchema = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    phone: z.string().trim().max(40).optional(),
    tradeSlug: z.string().trim().min(1).max(80).optional(),
    tradeType: z.string().trim().max(80).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.tradeSlug === OTHER_TRADE_SLUG && !value.tradeType?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter your trade",
        path: ["tradeType"],
      });
    }
  });

export type UpdateOrganizationInput = z.infer<typeof UpdateOrganizationSchema>;

export const OrganizationInvitationSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  email: z.string().email(),
  role: OrganizationInviteRoleSchema,
  status: OrganizationInvitationStatusSchema,
  expiresAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime().optional(),
  token: z.string().optional(),
});

export type OrganizationInvitation = z.infer<
  typeof OrganizationInvitationSchema
>;

export const PendingOrganizationInvitationSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  role: OrganizationInviteRoleSchema,
  status: OrganizationInvitationStatusSchema,
  invitedByName: z.string().nullable().optional(),
  expiresAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime().optional(),
  currentOrganizationName: z.string().nullable().optional(),
});

export type PendingOrganizationInvitation = z.infer<
  typeof PendingOrganizationInvitationSchema
>;

export const UpdateOrganizationMemberRoleSchema = z.object({
  role: OrganizationInviteRoleSchema,
});

export type UpdateOrganizationMemberRoleInput = z.infer<
  typeof UpdateOrganizationMemberRoleSchema
>;

export const CreateOrganizationInvitationSchema = z.object({
  email: z.string().trim().email(),
  role: OrganizationInviteRoleSchema.default("member"),
});

export type CreateOrganizationInvitationInput = z.input<
  typeof CreateOrganizationInvitationSchema
>;

export const OrganizationWebsiteSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  businessName: z.string().nullable(),
  ownerName: z.string().nullable(),
  phone: z.string().nullable(),
  tradeType: z.string().nullable(),
  serviceArea: z.string().nullable(),
  services: z.string().nullable(),
  subdomain: z.string().nullable(),
  siteUrl: z.string().url().nullable().optional(),
});

export type OrganizationWebsite = z.infer<typeof OrganizationWebsiteSchema>;

export const ServiceAreaNeighborhoodSchema = z.object({
  name: z.string().trim().min(1).max(200),
  lat: z.number(),
  lng: z.number(),
});

export type ServiceAreaNeighborhood = z.infer<
  typeof ServiceAreaNeighborhoodSchema
>;

export const CreateOrganizationWebsiteSchema = z.object({
  serviceArea: z.string().trim().min(1).max(200),
  neighborhoods: z.array(ServiceAreaNeighborhoodSchema).max(40).optional(),
});

export type CreateOrganizationWebsiteInput = z.infer<
  typeof CreateOrganizationWebsiteSchema
>;

export const UpdateOrganizationWebsiteSchema = z.object({
  ownerName: z.string().trim().min(1).max(100).optional(),
  businessName: z.string().trim().min(1).max(120).optional(),
  phone: z.string().trim().min(1).max(40).optional(),
  serviceArea: z.string().trim().min(1).max(200).optional(),
  services: z.string().trim().min(1).max(2000).optional(),
  tradeType: z.string().trim().min(1).max(80).optional(),
});

export type UpdateOrganizationWebsiteInput = z.infer<
  typeof UpdateOrganizationWebsiteSchema
>;

export const SelectOrganizationProductResponseSchema =
  OrganizationSchema.extend({
    next: z
      .enum(["jobs", "website", "create_website", "credentials"])
      .optional(),
    checkoutUrl: z.string().url().optional(),
  });

export type SelectOrganizationProductResponse = z.infer<
  typeof SelectOrganizationProductResponseSchema
>;

export const ORGANIZATION_REQUIRED_CODE = "organization_required";
export const ALREADY_IN_ORGANIZATION_CODE = "already_in_organization";
export const WEBSITE_MISSING_CODE = "website_missing";
export const WEBSITE_EXISTS_CODE = "website_exists";

export const ORGANIZATION_ROLE_DESCRIPTIONS: Record<OrganizationRole, string> =
  {
    owner: "Full access to the company.",
    admin:
      "Can manage the company website, jobs, credentials roster, team, and billing.",
    member:
      "Can capture job photos, create job sites, and edit or archive sites they created. They manage their own credentials, Home, and Settings.",
    viewer: "Read-only access to jobs and credentials they are allowed to see.",
  };

export type InviteRoleLegendEntry = {
  role: OrganizationInviteRole;
  description: string;
};

/** Admin/member/viewer copy for Team invite UI. Omit a role when its string is missing. */
export function inviteRoleLegendEntries(
  descriptions?: Record<string, string> | null,
): InviteRoleLegendEntry[] {
  if (!descriptions) return [];
  return ORGANIZATION_INVITE_ROLES.flatMap((role) => {
    const description = descriptions[role];
    return description ? [{ role, description }] : [];
  });
}

const NO_ORGANIZATION_CAPABILITIES: OrganizationCapabilities = {
  canCapturePhotos: false,
  canCreateJobs: false,
  canManageJobs: false,
  canManageAllPhotos: false,
  canManageWebsite: false,
  canUseCredentials: false,
};

const ORGANIZATION_ROLE_CAPABILITIES: Record<
  OrganizationRole,
  OrganizationCapabilities
> = {
  owner: {
    canCapturePhotos: true,
    canCreateJobs: true,
    canManageJobs: true,
    canManageAllPhotos: true,
    canManageWebsite: true,
    canUseCredentials: true,
  },
  admin: {
    canCapturePhotos: true,
    canCreateJobs: true,
    canManageJobs: true,
    canManageAllPhotos: true,
    canManageWebsite: true,
    canUseCredentials: true,
  },
  member: {
    canCapturePhotos: true,
    canCreateJobs: true,
    canManageJobs: false,
    canManageAllPhotos: false,
    canManageWebsite: false,
    canUseCredentials: true,
  },
  viewer: {
    ...NO_ORGANIZATION_CAPABILITIES,
    canUseCredentials: true,
  },
};

export function getOrganizationMembershipCapabilities(
  membership: OrganizationMembership | null | undefined,
): OrganizationCapabilities {
  if (!membership) return NO_ORGANIZATION_CAPABILITIES;
  return ORGANIZATION_ROLE_CAPABILITIES[membership.role];
}

export function getOrganizationCapabilities(
  organization:
    | Pick<Organization, "membership" | "entitlements" | "capabilities">
    | null
    | undefined,
): OrganizationCapabilities {
  if (organization?.capabilities) {
    return organization.capabilities;
  }

  const membershipCapabilities = getOrganizationMembershipCapabilities(
    organization?.membership,
  );
  const hasJobSitePhotos = Boolean(organization?.entitlements?.jobSitePhotos);
  const hasCredentials = Boolean(organization?.entitlements?.credentials);

  return {
    ...membershipCapabilities,
    canCapturePhotos:
      membershipCapabilities.canCapturePhotos && hasJobSitePhotos,
    canCreateJobs: membershipCapabilities.canCreateJobs && hasJobSitePhotos,
    canManageJobs: membershipCapabilities.canManageJobs && hasJobSitePhotos,
    canManageAllPhotos:
      membershipCapabilities.canManageAllPhotos && hasJobSitePhotos,
    canUseCredentials:
      membershipCapabilities.canUseCredentials && hasCredentials,
  };
}

export function canManagePhoto(
  capabilities: Pick<
    OrganizationCapabilities,
    "canCapturePhotos" | "canManageAllPhotos"
  >,
  photoUserId: string | null | undefined,
  currentUserId: string | null | undefined,
): boolean {
  return (
    capabilities.canManageAllPhotos ||
    (capabilities.canCapturePhotos &&
      Boolean(currentUserId) &&
      Boolean(photoUserId) &&
      photoUserId === currentUserId)
  );
}

export function canManageOrganizationMember(
  actorRole: OrganizationRole | null | undefined,
  memberRole: OrganizationRole | null | undefined,
  isCurrentUser: boolean,
): boolean {
  if (isCurrentUser) return false;
  if (!actorRole || !memberRole) return false;
  if (memberRole === "owner") return false;
  if (memberRole === "admin" && actorRole !== "owner") return false;
  return actorRole === "owner" || actorRole === "admin";
}

export function isBackendMembership(
  membership: OrganizationMembership | null | undefined,
): boolean {
  return getOrganizationMembershipCapabilities(membership).canManageWebsite;
}

export function canContributeJobPhotos(
  membership: OrganizationMembership | null | undefined,
): boolean {
  return getOrganizationMembershipCapabilities(membership).canCapturePhotos;
}

export function canCaptureJobPhotos(
  organization:
    | Pick<Organization, "membership" | "entitlements">
    | null
    | undefined,
): boolean {
  return getOrganizationCapabilities(organization).canCapturePhotos;
}

export function canManageWebsite(
  organization:
    | Pick<Organization, "membership" | "entitlements">
    | null
    | undefined,
): boolean {
  return getOrganizationCapabilities(organization).canManageWebsite;
}

export function organizationProduct(
  organization: Pick<Organization, "products"> | null | undefined,
  key: OrganizationProductKey,
): OrganizationProduct | undefined {
  return organization?.products?.find((product) => product.key === key);
}

export function isOrganizationProductSelected(
  organization: Pick<Organization, "products"> | null | undefined,
  key: OrganizationProductKey,
): boolean {
  return Boolean(organizationProduct(organization, key)?.selectedAt);
}
